/**
 * Maps operation arrays to their corresponding operation objects based on the operation type
 * This utility function converts the internal operation structure to the API expected format
 */

/**
 * Maps operation arrays to their corresponding operation objects
 * @param operations Array of operation objects to map
 * @returns Mapped operation objects in the format expected by the API
 */
export const mapPatchOperations = (operations: any[], removal: boolean = false) => {
  // Enhanced input validation
  if (!operations || !Array.isArray(operations)) {
    console.warn('mapPatchOperations: Invalid operations input, expected array');
    return [];
  }

  if (operations.length === 0) {
    return [];
  }

  // Filter out invalid operations with detailed logging
  const validOperations = operations.filter((operation, index) => {
    if (!operation || typeof operation !== 'object') {
      console.warn(`mapPatchOperations: Invalid operation at index ${index}:`, operation);
      return false;
    }
    
    // Check for potential XSS in operation data
    const operationStr = JSON.stringify(operation);
    if (/<script|javascript:|on\w+=/i.test(operationStr)) {
      console.warn(`mapPatchOperations: Potentially dangerous content detected in operation ${index}`);
      return false;
    }
    
    return true;
  });

  return validOperations.map((operation) => {
    // Remove __typename if it exists and get the actual operation data
    const { __typename, ...cleanOperation } = operation;
    
    // Determine the operation type from the object keys (excluding __typename)
    const operationKeys = Object.keys(cleanOperation);

    // Only process if we have exactly one operation key
    if (operationKeys.length !== 1) {
      console.warn('Operation has unexpected format:', operation);
      return operation;
    }

    const operationType = operationKeys[0];
    const operationData = cleanOperation[operationType];

    switch (operationType) {
      case 'setVariantPrice':
        if (removal) {
          // For removal operations, only include essential identifiers
          return {
            setVariantPrice: {
              variantId: operationData.variant?.variantId ?? operationData.variantId,
            },
          };
        }
        return {
          setVariantPrice: {
            ...(operationData.product && {
              productId: operationData.product.productId,
            }),
            variantId: operationData.variant?.variantId ?? operationData.variantId,
            ...(operationData.authoringPrice && {
              authoringPrice: {
                amount: operationData.authoringPrice.amount,
              },
            }),
          },
        };

      case 'setModifierPriceRanges':
        if (removal) {
          // For removal operations, only include essential identifiers - no weightPriceRanges
          return {
            setModifierPriceRanges: {
              variantId: operationData.variant?.variantId ?? operationData.variantId,
              slotId: operationData.slot?.slotId ?? operationData.slotId,
            },
          };
        }
        return {
          setModifierPriceRanges: {
            variantId: operationData.variant?.variantId ?? operationData.variantId,
            slotId: operationData.slot?.slotId ?? operationData.slotId,
            weightPriceRanges:
              operationData.weightPriceRanges?.map((range: any) => ({
                rangeStart: range.rangeStart,
                rangeEnd: range.rangeEnd,
                ...(range.authoringPrice?.amount !== undefined && {
                  authoringPrice: {
                    amount: range.authoringPrice.amount,
                  },
                }),
                applyModifierWeightUpcharge: range.applyModifierWeightUpcharge ?? true,
              })) || [],
          },
        };

      case 'setModifierWeightPrice':
        if (removal) {
          // For removal operations, only include essential identifiers
          return {
            setModifierWeightPrice: {
              variantId: operationData.variant?.variantId ?? operationData.variantId,
              slotId: operationData.slot?.slotId ?? operationData.slotId,
              modifierId: operationData.modifier?.modifierId ?? operationData.modifierId,
              weightId: operationData.weight?.weightId ?? operationData.weightId,
            },
          };
        }
        return {
          setModifierWeightPrice: {
            variantId: operationData.variant?.variantId ?? operationData.variantId,
            slotId: operationData.slot?.slotId ?? operationData.slotId,
            modifierId: operationData.modifier?.modifierId ?? operationData.modifierId,
            weightId: operationData.weight?.weightId ?? operationData.weightId,
            ...(operationData.authoringPrice && {
              authoringPrice: {
                amount: operationData.authoringPrice.amount,
              },
            }),
          },
        };

      case 'removeProduct':
        return {
          removeProduct: {
            productId: operationData.product?.productId ?? operationData.productId,
          },
        };

      case 'removeVariant':
        return {
          removeVariant: {
            variantId: operationData.variant?.variantId ?? operationData.variantId,
          },
        };

      case 'removeModifier':
        return {
          removeModifier: {
            modifierId: operationData.modifier?.modifierId ?? operationData.modifierId,
          },
        };

      case 'removeModifierFromSlot':
        return {
          removeModifierFromSlot: {
            slotId: operationData.slotId ?? operationData.slot?.slotId,
            modifierId: operationData.modifierId ?? operationData.modifier?.modifierId,
          },
        };

      case 'removeWeight':
        return {
          removeWeight: {
            weightId: operationData.weightId ?? operationData.weight?.weightId,
          },
        };

      case 'setModifierWeightIsExcludedFromVariant':
        if (removal) {
          // For removal operations, only include essential identifiers - no boolean value
          return {
            setModifierWeightIsExcludedFromVariant: {
              variantId: operationData.variantId ?? operationData.variant?.variantId,
              slotId: operationData.slotId ?? operationData.slot?.slotId,
              modifierId: operationData.modifierId ?? operationData.modifier?.modifierId,
              weightId: operationData.weightId ?? operationData.weight?.weightId,
            },
          };
        }
        return {
          setModifierWeightIsExcludedFromVariant: {
            variantId: operationData.variantId ?? operationData.variant?.variantId,
            slotId: operationData.slotId ?? operationData.slot?.slotId,
            modifierId: operationData.modifierId ?? operationData.modifier?.modifierId,
            weightId: operationData.weightId ?? operationData.weight?.weightId,
            isExcludedFromVariant: operationData.isExcludedFromVariant ?? operationData.isExcluded ?? true,
          },
        };

      case 'setBundlePrice':
        if (removal) {
          // For removal operations, only include essential identifiers
          return {
            setBundlePrice: {
              bundleId: operationData.bundle?.bundleId ?? operationData.bundleId,
            },
          };
        }
        return {
          setBundlePrice: {
            bundleId: operationData.bundle?.bundleId ?? operationData.bundleId,
            ...(operationData.authoringPrice && {
              authoringPrice: {
                amount: operationData.authoringPrice.amount,
              },
            }),
          },
        };

      case 'removeBundle':
        return {
          removeBundle: {
            bundleId: operationData.bundle?.bundleId ?? operationData.bundleId,
          },
        };

      default:
        console.warn(`Unknown operation type: ${operationType}`, operation);
        return operation;
    }
  });
};

/**
 * Type-safe way to determine if an object has a specific property
 * @param obj The object to check
 * @param prop The property to check for
 * @returns Boolean indicating if the property exists
 */
export const hasProperty = <T, K extends PropertyKey>(obj: T, prop: K): obj is T & Record<K, unknown> => {
  return Object.prototype.hasOwnProperty.call(obj, prop);
};

/**
 * Helper function to extract operation type from a patch operation
 * @param operation The operation object
 * @returns The operation type as a string or 'unknown'
 */
export const getOperationType = (operation: any): string => {
  if (!operation) return 'unknown';

  const keys = Object.keys(operation);
  if (keys.length !== 1) return 'unknown';

  return keys[0];
};
